// Client blackboard: the single GameState that net writes into, input/ui mutate
// intent on, and render reads. Plus a tiny pub/sub for UI updates.

import { chunkKey, chunkKeyOf, localIdx } from '../shared/hex'
import type {
  ClientMsg, LeaderRow, SelfInfo, ServerMsg } from '../shared/protocol'
import type { Building, BuildingType, FactionPublic, GameEvent, ItemType, SpillPile, UnitWire } from '../shared/types'

/** World pixels per hex (center-to-corner) at zoom 1. */
export const HEX_SIZE = 24

const EVENTS_CAP = 100
const CHAT_CAP = 100

export interface ClientChunk {
  cx: number
  cy: number
  tiles: number[]
  roads: Set<number>
  owners: Map<number, string>
  /** Ground items by local hex index — a slain unit's dropped tool/weapon. */
  drops: Map<number, ItemType>
  /** Spilled resource piles by local hex index — a razed town hall's stores. */
  spills: Map<number, SpillPile>
  /** Optional per-tile height tiers (parallel to tiles) — present only when the
   *  server's elevation feature is on; undefined on flat worlds. */
  elev?: number[]
  /** Optional per-tile standing-tree flags (1 = a choppable tree grows on this
   *  grass hex), parallel to tiles. Undefined on chunks with no woodland. */
  trees?: number[]
  /** Explored before this session (server-stamped): fog may treat every hex of
   *  this chunk as already seen. Sticky — a later in-vision refresh of the same
   *  chunk must not demote remembered ground to void. */
  mem?: boolean
  version: number
}

/** Is the WebGL 3D view active? 3D is the default; ?classic or the persisted
 *  hexClassic setting opts back into the 2.5D canvas; ?r3d always forces 3D. */
export function use3dView(): boolean {
  const params = new URLSearchParams(location.search)
  if (params.has('r3d')) return true
  if (params.has('classic')) return false
  try { return localStorage.getItem('hexClassic') !== '1' } catch { return true }
}

export interface GameState {
  me: string | null
  self: SelfInfo | null
  home: { q: number; r: number } | null
  tick: number
  connected: boolean
  chunks: Map<string, ClientChunk>
  buildings: Map<number, Building>
  units: Map<number, UnitWire>
  factions: Map<string, FactionPublic>
  /** Ring buffer, newest last, cap 100. */
  events: GameEvent[]
  /** Cap 100, newest last. */
  chat: { from: string; name: string; text: string }[]
  /** Public standings (top 3 kingdoms by hexes/serfs), null until first received. */
  leaderboard: { hexes: LeaderRow[]; serfs: LeaderRow[] } | null
  // interaction state
  /** World-px center; zoom 0.25..6 (hex size on screen = HEX_SIZE * zoom).
   *  rot: view spin target in 60° steps (Q/E), 0 = north-up (unbounded, read mod 6).
   *  rotF: the eased current spin index (float) that glides toward rot — the animation. */
  cam: { x: number; y: number; zoom: number; rot: number; rotF: number }
  hover: { q: number; r: number } | null
  /** Entity under the cursor (3D view raycast) — drives the hover silhouette. */
  hoverEnt: { kind: 'unit' | 'building'; id: number } | null
  /** UI-driven highlight (e.g. hovering a serf's workplace chip in the panel) —
   *  both renderers silhouette this building exactly like a cursor hover. */
  uiHover: { kind: 'building'; id: number } | null
  /** Last cursor position in canvas/screen px (null off-canvas); drives hover pick. */
  mouse: { x: number; y: number } | null
  selection: { units: number[]; building: number | null }
  buildMode: BuildingType | null
  /** Client-only: the ordered hexes of an in-progress drag-paint stroke (road / dig /
   *  fill). Shown as a live highlight while the button is held and committed to the
   *  server only on release; back-dragging over an earlier hex truncates the path to
   *  it (un-highlighting the rest). Empty whenever no stroke is in progress. Each hex
   *  caches `ok` (its ghostValid verdict at the moment it was added) so the renderer
   *  colours it without recomputing every frame. */
  paintPath: { q: number; r: number; ok: boolean }[]
  /** The build type captured when the current paint stroke began — the highlight and
   *  the on-release commit use THIS, not the live buildMode, so nothing changes type
   *  mid-stroke (e.g. a stray number-key). null when no stroke is in progress. */
  paintType: BuildingType | null
  /** Box-order sub-mode: false = queue forestry or earthwork; true = remove orders
   *  (including growing saplings). Reset whenever a box-order tool is armed. */
  chopCancel: boolean
  /** Pier placement: rotation index cycling the valid orientations at the hover
   *  hex (R key / ⟳ touch chip). Applied modulo the count, so any value is safe. */
  pierRotate: number
  /**
   * Actual on-screen world-px position of each unit, published by the renderer
   * every frame (post smoothing + slot offset). Input hit-tests against this so
   * clicks land on the figure you actually see, not an idealized position.
   */
  unitPos: Map<number, { x: number; y: number; ax?: number; ay?: number; bx?: number; by?: number; t0?: number }>
  /** Admin spectator session: fog-free, read-only. */
  observer: boolean
  /** True once any chunk carrying elevation has arrived — gates elevation-only
   *  UI (the ramp build button) so a flat world's interface is unchanged. */
  hasElev: boolean
  /** 2.5D faux-isometric height lift on/off (toggle with the L key). Only has any
   *  effect on elevation worlds; flat worlds always render top-down. */
  lift25: boolean
  /** Connection-quality metrics: when the last tick landed (ms epoch), the worst
   *  inter-tick gap seen, how many long (>1.5s) gaps, and the measured RTT.
   *  Feeds the catching-up chip and lag probes. */
  net: { lastTickAt: number; gapMaxMs: number; longGaps: number; rttMs: number }
  /** Hex → CSS-pixel screen position, published fresh each frame by whichever
   *  renderer is active (3D camera projection / 2.5D canvas transform). Lets
   *  DOM overlays (the tutorial spotlight) anchor to world objects. */
  projectHex: ((q: number, r: number) => { x: number; y: number; vis: boolean }) | null
  // ui pub/sub
  on(evt: string, fn: (data?: any) => void): void
  emit(evt: string, data?: any): void
}

/**
 * Remembers the last version used for each chunk key even across a
 * welcome-triggered clear, so re-received chunks always get a strictly higher
 * version and the renderer re-caches them.
 */
interface StateInternal extends GameState {
  _chunkVer: Map<string, number>
}

export function createState(): GameState {
  const listeners = new Map<string, Set<(data?: any) => void>>()
  const st: StateInternal = {
    me: null,
    self: null,
    home: null,
    tick: 0,
    connected: false,
    chunks: new Map(),
    buildings: new Map(),
    units: new Map(),
    factions: new Map(),
    events: [],
    chat: [],
    leaderboard: null,
    cam: { x: 0, y: 0, zoom: 1, rot: 0, rotF: 0 },
    hover: null,
    hoverEnt: null,
    uiHover: null,
    mouse: null,
    selection: { units: [], building: null },
    buildMode: null,
    paintPath: [],
    paintType: null,
    chopCancel: false,
    pierRotate: 0,
    unitPos: new Map(),
    observer: false,
    hasElev: false,
    net: { lastTickAt: 0, gapMaxMs: 0, longGaps: 0, rttMs: 0 },
    projectHex: null,
    lift25: (() => { try { return localStorage.getItem('hexFlat') !== '1' } catch { return true } })(),
    on(evt, fn) {
      let set = listeners.get(evt)
      if (!set) listeners.set(evt, (set = new Set()))
      set.add(fn)
    },
    emit(evt, data) {
      const set = listeners.get(evt)
      if (!set) return
      for (const fn of [...set]) {
        try {
          fn(data)
        } catch (err) {
          console.error(`[state] listener for "${evt}" threw`, err)
        }
      }
    },
    _chunkVer: new Map(),
  }
  return st
}

function bumpVersion(st: GameState, key: string, current: number | undefined): number {
  const vm = (st as StateInternal)._chunkVer as Map<string, number> | undefined
  const prev = current ?? vm?.get(key) ?? 0
  const v = prev + 1
  vm?.set(key, v)
  return v
}

/** Drop selection ids that no longer refer to a live building/unit. Allocation-light. */
function pruneSelection(st: GameState): void {
  const sel = st.selection
  if (sel.building !== null && !st.buildings.has(sel.building)) sel.building = null
  if (sel.units.length) {
    const kept = sel.units.filter((id) => st.units.has(id))
    if (kept.length !== sel.units.length) sel.units = kept
  }
}

/**
 * Remove ghost entities: buildings/units whose (q,r) falls outside every
 * currently-loaded chunk (they died or walked out of view while we weren't
 * subscribed, so no removal message ever arrived), then prune the selection of
 * any ids that just disappeared. Call periodically. Allocation-light.
 */
export function cullGhosts(st: GameState): void {
  const chunks = st.chunks
  for (const [id, b] of st.buildings) {
    if (!chunks.has(chunkKeyOf(b.q, b.r))) st.buildings.delete(id)
  }
  for (const [id, u] of st.units) {
    // Own people now stream independently of the camera. Keep them while
    // their terrain loads; authoritative ru messages handle death/removal.
    if (u.owner !== st.me && !chunks.has(chunkKeyOf(u.q, u.r))) st.units.delete(id)
  }
  pruneSelection(st)
}

/** Optimistic local echo: apply a command's expected effect immediately so
 *  panels feel instant. The server's authoritative building update (sent even
 *  on rejection) overwrites this a moment later. */
export function predictLocal(st: GameState, m: ClientMsg): void {
  if (!('id' in m) || typeof (m as { id?: unknown }).id !== 'number') return
  const b = st.buildings.get((m as { id: number }).id)
  if (!b || b.owner !== st.me) return
  switch (m.t) {
    case 'prio':
      if (m.prio === 0) delete b.prio
      else b.prio = Math.max(-3, Math.min(3, m.prio))
      break
    case 'sprio':
      if (m.prio === 0) delete b.sprio
      else b.sprio = Math.max(-3, Math.min(3, m.prio))
      break
    case 'pause':
      if (m.paused) b.paused = true
      else delete b.paused
      break
    case 'store': {
      if (typeof m.accept === 'boolean') {
        if (m.accept) {
          if (b.accept) delete b.accept[m.res]
        } else {
          ;(b.accept ??= {})[m.res] = false
        }
      }
      const lim = (b.lim ??= {})
      const entry = (lim[m.res] ??= {})
      if (m.min !== undefined) {
        if (m.min === null) delete entry.min
        else entry.min = m.min
      }
      if (m.max !== undefined) {
        if (m.max === null) delete entry.max
        else entry.max = m.max
      }
      break
    }
    case 'crew': {
      if (m.add) {
        for (const ob of st.buildings.values()) {
          if (ob.crew) ob.crew = ob.crew.filter((id) => id !== m.unit)
        }
        ;(b.crew ??= []).push(m.unit)
      } else if (b.crew) {
        b.crew = b.crew.filter((id) => id !== m.unit)
      }
      break
    }
    default:
      return
  }
  st.emit('predict')
}

export function applyServer(st: GameState, msg: ServerMsg): void {
  switch (msg.t) {
    case 'welcome': {
      st.me = msg.you
      st.observer = msg.observer === true
      if (st.observer) {
        // deep-link: /?observe=…&at=q,r centers the camera there
        const at = new URLSearchParams(location.search).get('at')
        const m2 = at?.match(/^(-?\d+),(-?\d+)$/)
        if (m2) msg.home = { q: Number(m2[1]), r: Number(m2[2]) }
      }
      st.self = msg.self
      st.home = { q: msg.home.q, r: msg.home.r }
      st.tick = msg.tick
      st.factions.clear()
      for (const f of msg.factions) st.factions.set(f.id, f)
      // Drop any stale world data from a previous connection: the server will
      // re-send chunks/entities for everything we (re)subscribe to. Remember
      // old chunk versions so re-received chunks still bump strictly upward.
      const vm = (st as StateInternal)._chunkVer as Map<string, number> | undefined
      if (vm) for (const [key, c] of st.chunks) vm.set(key, c.version)
      st.chunks.clear()
      st.buildings.clear()
      st.units.clear()
      st.selection = { units: [], building: null }
      st.paintPath = [] // a reconnect/re-welcome must not leave a stale stroke highlight
      st.paintType = null
      st.chopCancel = false
      st.emit('welcome', msg)
      st.emit('factions')
      st.emit('self', st.self)
      break
    }
    case 'chunk': {
      const key = chunkKey(msg.cx, msg.cy)
      const roads = new Set<number>(msg.roads)
      const owners = new Map<number, string>()
      for (const [idx, owner] of Object.entries(msg.owners)) owners.set(Number(idx), owner)
      const drops = new Map<number, ItemType>()
      if (msg.drops) for (const [idx, item] of Object.entries(msg.drops)) drops.set(Number(idx), item)
      const spills = new Map<number, SpillPile>()
      if (msg.spills) for (const [idx, pile] of Object.entries(msg.spills)) spills.set(Number(idx), pile)
      if (msg.elev && !st.hasElev) st.hasElev = true
      const existing = st.chunks.get(key)
      st.chunks.set(key, {
        cx: msg.cx,
        cy: msg.cy,
        tiles: msg.tiles,
        roads,
        owners,
        drops,
        spills,
        elev: msg.elev,
        trees: msg.trees,
        mem: msg.mem || existing?.mem,
        version: bumpVersion(st, key, existing?.version),
      })
      break
    }
    case 'ent': {
      if (msg.b) for (const b of msg.b) st.buildings.set(b.id, b)
      if (msg.u) for (const u of msg.u) st.units.set(u.id, u)
      if (msg.rb) {
        for (const id of msg.rb) {
          st.buildings.delete(id)
          if (st.selection.building === id) st.selection = { units: st.selection.units, building: null }
        }
      }
      if (msg.ru) {
        for (const id of msg.ru) st.units.delete(id)
        if (st.selection.units.length) {
          const kept = st.selection.units.filter((id) => st.units.has(id))
          if (kept.length !== st.selection.units.length)
            st.selection = { units: kept, building: st.selection.building }
        }
      }
      break
    }
    case 'tick': {
      // Connection-quality bookkeeping: ticks arrive ~1/s; the gap between
      // them is the truest lag signal the client has.
      const now = Date.now()
      if (st.net.lastTickAt > 0) {
        const gap = now - st.net.lastTickAt
        if (gap > st.net.gapMaxMs) st.net.gapMaxMs = gap
        if (gap > 1500) st.net.longGaps++
      }
      st.net.lastTickAt = now
      st.tick = msg.tick
      st.emit('tick', msg.tick)
      break
    }
    case 'self': {
      st.self = msg.self
      st.emit('self', msg.self)
      break
    }
    case 'faction': {
      st.factions.set(msg.f.id, msg.f)
      st.emit('factions', msg.f)
      break
    }
    case 'events': {
      for (const ev of msg.list) st.events.push(ev)
      if (st.events.length > EVENTS_CAP) st.events.splice(0, st.events.length - EVENTS_CAP)
      st.emit('events', msg.list)
      break
    }
    case 'chat': {
      const entry = { from: msg.from, name: msg.name, text: msg.text }
      st.chat.push(entry)
      if (st.chat.length > CHAT_CAP) st.chat.splice(0, st.chat.length - CHAT_CAP)
      st.emit('chat', entry)
      break
    }
    case 'diplo': {
      st.emit('diplo', msg)
      break
    }
    case 'leaderboard': {
      st.leaderboard = { hexes: msg.hexes, serfs: msg.serfs }
      st.emit('leaderboard', st.leaderboard)
      break
    }
    case 'tile': {
      const key = chunkKeyOf(msg.q, msg.r)
      const c = st.chunks.get(key)
      if (!c) break // tile in a chunk we haven't loaded; nothing to update
      const idx = localIdx(msg.q, msg.r)
      let changed = false
      if (msg.terrain !== undefined && c.tiles[idx] !== msg.terrain) {
        c.tiles[idx] = msg.terrain
        changed = true
      }
      if (msg.elev !== undefined && c.elev && c.elev[idx] !== msg.elev) {
        c.elev[idx] = msg.elev // terraform: the hex's height tier changed
        changed = true
      }
      if (msg.tree !== undefined) {
        const cur = c.trees?.[idx] === 1
        if (cur !== msg.tree) {
          // A tree was felled (chop) or grew (regrowth): flip the flag so the
          // renderer drops/adds its canopy. Allocate the array on first tree.
          if (msg.tree) (c.trees ??= new Array<number>(c.tiles.length).fill(0))[idx] = 1
          else if (c.trees) c.trees[idx] = 0
          changed = true
        }
      }
      if (msg.road !== undefined) {
        if (msg.road) {
          if (!c.roads.has(idx)) {
            c.roads.add(idx)
            changed = true
          }
        } else if (c.roads.delete(idx)) {
          changed = true
        }
      }
      if (msg.owner !== undefined) {
        if (msg.owner === '') {
          if (c.owners.delete(idx)) changed = true
        } else if (c.owners.get(idx) !== msg.owner) {
          c.owners.set(idx, msg.owner)
          changed = true
        }
      }
      if (msg.drop !== undefined) {
        if (msg.drop === '') {
          if (c.drops.delete(idx)) changed = true
        } else if (c.drops.get(idx) !== msg.drop) {
          c.drops.set(idx, msg.drop)
          changed = true
        }
      }
      if (msg.spill !== undefined) {
        if (msg.spill === '') {
          if (c.spills.delete(idx)) changed = true
        } else {
          c.spills.set(idx, msg.spill)
          changed = true
        }
      }
      if (changed) c.version = bumpVersion(st, key, c.version)
      break
    }
    case 'error': {
      st.emit('error', msg.msg)
      break
    }
    case 'reload': {
      st.emit('reload', msg.reason)
      break
    }
    case 'pong':
      break
  }
}
